import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Check, X, Loader2 } from 'lucide-react';
import { API_URL } from '../../utils/apiConfig';
import { authStorage } from '../../utils/storage';
import { toast } from '../../hooks/use-toast';
import LogoutConfirmModal from '../shared/LogoutConfirmModal';

const YachanguPersonalInfoPage = ({ user, onLogout, onBack }) => {
  const [currentUser, setCurrentUser] = useState(user || {});
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);

  const countryCode = currentUser?.country_code || '+265';

  const startEditPhone = () => {
    setPhoneInput(currentUser?.phone || '');
    setEditingPhone(true);
  };

  const savePhone = async () => {
    setSavingPhone(true);
    try {
      const res = await axios.put(`${API_URL}/users/profile/${currentUser.id}`, {
        phone: phoneInput.trim(),
        country_code: countryCode,
      });
      if (res.data?.success) {
        const updated = res.data.user;
        setCurrentUser(updated);
        // Persist so the change survives reloads and is used app-wide next launch.
        try {
          const auth = await authStorage.getAuth();
          if (auth?.token) await authStorage.saveAuth({ token: auth.token, user: updated }, true);
        } catch (_) { /* noop */ }
        setEditingPhone(false);
        toast({ title: 'Phone number updated' });
      } else {
        toast({ title: 'Could not update phone', description: res.data?.detail || 'Please try again', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Could not update phone', description: err.response?.data?.detail || 'Please try again', variant: 'destructive' });
    } finally {
      setSavingPhone(false);
    }
  };

  return (
    <div className="absolute inset-0 bg-white dark:bg-gray-800 z-50 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4 flex items-center">
        <button
          onClick={onBack}
          className="w-10 h-10 flex items-center justify-center text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white ml-2">Personal info</h1>
      </div>

      {/* Profile Photo Section */}
      <div className="bg-white dark:bg-gray-800 px-6 py-8">
        <div className="flex flex-col items-center">
          {/* Profile Photo Circle */}
          <div className="relative w-32 h-32 mb-4">
            <div className="w-full h-full bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-16 h-16 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            </div>
            {/* Green Plus Icon */}
            <button className="absolute top-0 right-0 w-10 h-10 bg-green-600 hover:bg-green-700 rounded-full flex items-center justify-center shadow-lg transition-colors">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>

          {/* Description Text */}
          <p className="text-center text-base font-medium text-gray-900 dark:text-white mb-2 px-4">
            Add profile photo so drivers can recognize you easily during pickup
          </p>

          {/* Info Link */}
          <button className="text-green-600 hover:text-green-700 text-sm font-medium transition-colors">
            When can the driver see my photo?
          </button>
        </div>
      </div>

      {/* Personal Information Fields */}
      <div className="bg-white dark:bg-gray-800">
        {/* Name Field */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center space-x-4 flex-1">
            <div className="w-6 h-6 text-gray-400">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-base text-gray-900 dark:text-white">
              {currentUser?.first_name || currentUser?.firstName || 'User'} {currentUser?.last_name || currentUser?.lastName || ''}
            </span>
          </div>
          <button
            onClick={() => toast({ title: 'Name is used for ID verification', description: 'Contact support to change your legal name.' })}
            className="text-green-600 hover:text-green-700 font-medium text-base transition-colors"
          >
            Edit
          </button>
        </div>

        {/* Phone Field */}
        <div className="px-6 py-4 border-b border-gray-100">
          {!editingPhone ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 flex-1">
                <div className="w-6 h-6 text-gray-400">
                  <Phone className="w-6 h-6" />
                </div>
                <span className="text-base text-gray-900 dark:text-white" data-testid="personal-phone-value">
                  {currentUser?.phone ? `${countryCode} ${currentUser.phone}` : 'Add phone number'}
                </span>
              </div>
              <button
                onClick={startEditPhone}
                data-testid="personal-phone-edit"
                className="text-green-600 hover:text-green-700 font-medium text-base transition-colors"
              >
                {currentUser?.phone ? 'Edit' : 'Add'}
              </button>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone Number</label>
              <div className="flex items-stretch gap-2">
                <div className="flex items-center px-3 rounded-xl bg-gray-100 border border-gray-300 text-gray-700 font-medium">
                  {countryCode}
                </div>
                <input
                  type="tel"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value.replace(/[^\d]/g, '').slice(0, 10))}
                  placeholder="0991 234 567"
                  autoFocus
                  data-testid="personal-phone-input"
                  className="flex-1 h-11 px-4 rounded-xl bg-white border border-gray-300 text-gray-900 placeholder-gray-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all"
                />
                <button
                  onClick={savePhone}
                  disabled={savingPhone}
                  data-testid="personal-phone-save"
                  className="w-11 h-11 flex items-center justify-center rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50 transition-colors"
                  aria-label="Save phone"
                >
                  {savingPhone ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => setEditingPhone(false)}
                  disabled={savingPhone}
                  data-testid="personal-phone-cancel"
                  className="w-11 h-11 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600 disabled:opacity-50 transition-colors"
                  aria-label="Cancel"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1.5">Enter your number starting with 0 (e.g. 0991 234 567).</p>
            </div>
          )}
        </div>

        {/* Email Field */}
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center space-x-4 flex-1">
            <div className="w-6 h-6 text-gray-400">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
            </div>
            <span className="text-base text-gray-900 dark:text-white">
              {currentUser?.email || 'Add email address'}
            </span>
          </div>
        </div>
      </div>

      {/* Account Actions Section */}
      <div className="mt-8 mb-24">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white px-6 pb-3">
          Account actions
        </h3>
        <div className="bg-white dark:bg-gray-800">
          {/* Log out */}
          <button
            onClick={() => setShowLogoutConfirm(true)}
            data-testid="rider-logout-btn"
            className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 active:bg-gray-100 transition-colors border-b border-gray-100"
          >
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-xl">🚪</span>
              </div>
              <div className="text-left">
                <div className="font-medium text-gray-900 dark:text-white">Log out</div>
              </div>
            </div>
            <span className="text-gray-400">›</span>
          </button>

          {/* Delete account */}
          <button
            onClick={() => console.log('Delete account')}
            className="w-full flex items-center justify-between px-6 py-4 hover:bg-red-50 active:bg-red-100 transition-colors"
          >
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-xl">🗑️</span>
              </div>
              <div className="text-left">
                <div className="font-medium text-red-500">Delete account</div>
              </div>
            </div>
          </button>
        </div>
      </div>

      <LogoutConfirmModal
        open={showLogoutConfirm}
        onCancel={() => setShowLogoutConfirm(false)}
        onConfirm={() => { setShowLogoutConfirm(false); onLogout?.(); }}
      />
    </div>
  );
};

export default YachanguPersonalInfoPage;
